import React, { useState, useRef, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import auth from '../../utils/auth';

const LoginForm = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const [newPasswordRequired, setNewPasswordRequired] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [userSession, setUserSession] = useState(null);
    const requiredAttributesRef = useRef([]);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [requiresMFA, setRequiresMFA] = useState(false);
    const [otpCode, setOtpCode] = useState('');
    const history = useHistory();

    useEffect(() => {
        // If user is already logged in, redirect to dashboard
        if (auth.isAuthenticated()) {
            history.push('/dashboard');
        }
    }, [history]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        
        try {
            // Use the auth utility instead of direct axios calls
            const response = await auth.login(email, password);

            // Check if login was unsuccessful
            if (!response.success) {
                setError(response.message || 'An error occurred during login');
                setIsLoading(false);
                return;
            }
            
            // Check if NEW_PASSWORD_REQUIRED challenge is present.
            // New-user setup returns it nested under `result` with ChallengeParameters.
            if (response.success &&
                response.result &&
                response.result.ChallengeName === "NEW_PASSWORD_REQUIRED") {
                
                // Store the session for the next request
                setUserSession(response.result.Session);
                
                // Check if there are required attributes in userAttributes
                try {
                    const userAttributes = JSON.parse(
                        response.result.ChallengeParameters.userAttributes
                    );
                    
                    // Pre-populate the form fields if available
                    if (userAttributes.given_name) setFirstName(userAttributes.given_name);
                    if (userAttributes.family_name) setLastName(userAttributes.family_name);
                    if (userAttributes.email) setEmail(userAttributes.email);
                    
                } catch (err) {
                    console.error("Error parsing user attributes", err);
                }
                
                // Set new password required state
                setNewPasswordRequired(true);

                // get required attributes (json parse)
                try {
                    const t_requiredAttributes = JSON.parse(
                        response.result.ChallengeParameters.requiredAttributes
                    );
                    // Store in ref for immediate access
                    requiredAttributesRef.current = t_requiredAttributes;
                } catch (err) {
                    console.error("Error parsing required attributes", err);
                }

            } else if (
                // Admin-initiated reset on an existing (already migrated) user puts the
                // account into FORCE_CHANGE_PASSWORD. The backend returns the challenge in a
                // flat shape (challengeName/session) with no ChallengeParameters.
                response.success &&
                response.challengeName === "NEW_PASSWORD_REQUIRED" &&
                response.session
            ) {

                // Store the session for the password-change request.
                setUserSession(response.session);

                // The email is already captured from the login form. The backend now passes
                // Cognito's challengeParameters through in this shape too, so use the real
                // required-attribute list when it is there and fall back to an empty list.
                requiredAttributesRef.current = [];
                if (response.challengeParameters) {
                    try {
                        requiredAttributesRef.current = JSON.parse(
                            response.challengeParameters.requiredAttributes
                        );
                    } catch (err) {
                        console.error("Error parsing required attributes", err);
                    }

                    // Pre-populate the name fields with whatever the user already has on file.
                    try {
                        const existingAttributes = JSON.parse(
                            response.challengeParameters.userAttributes
                        );
                        if (existingAttributes.given_name) setFirstName(existingAttributes.given_name);
                        if (existingAttributes.family_name) setLastName(existingAttributes.family_name);
                    } catch (err) {
                        console.error("Error parsing user attributes", err);
                    }
                }

                // Show the set-new-password form.
                setNewPasswordRequired(true);
                setIsLoading(false);
                return;

            }

            if (
                response.success &&
                response.requiresMFA &&
                response.challengeName === "CUSTOM_CHALLENGE"
            ) {

                // Store the session for the MFA request
                setUserSession(response.session);
                setRequiresMFA(true);
                setIsLoading(false);
                return;

            } else {

                // Successful login - token should already be stored by auth.login
                console.log("Login successful");
                // history.push('/dashboard');

            }


        } catch (err) {
            console.error("Login error", err);
            setError(err.response?.data?.message || err.message || 'An error occurred during login');
        }
        
        setIsLoading(false);
    };

    // handle password submit forms
    const handleNewPasswordSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const userAttributes = {
            given_name: firstName,
            family_name: lastName,
            email: email
            };

            const response = await auth.completePasswordChange(
            userSession,
            newPassword,
            userAttributes,
            requiredAttributesRef.current
            );

            // MFA via email OTP required
            if (response?.requiresMFA && response?.challengeName === 'CUSTOM_CHALLENGE') {
            setUserSession(response.session);
            setRequiresMFA(true);
            setNewPasswordRequired(false); // switch UI from new password form to OTP form
            setIsLoading(false);
            return;
            }

            if (response?.success) {
            history.push('/dashboard');
            return;
            }

            setError(response?.message || response?.error || 'Failed to set new password');
        } catch (err) {
            console.error('Password change error', err);
            setError(
            err?.error ||
            err?.response?.data?.error ||
            err?.message ||
            'Failed to set new password'
            );
        }

        setIsLoading(false);
    };

    // New function to handle OTP submission
    const handleOTPSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        
        try {
            const response = await auth.verifyOTP(email, userSession, otpCode);
            
            if (response.success && auth.isAuthenticated()) {

                setIsLoading(false);

                // Redirect to dashboard on successful OTP verification
                history.push('/dashboard');

            } else {
                setError(response.error || 'Failed to verify OTP code');
            }
        } catch (err) {
            console.error("OTP verification error", err);
            setError(
                err.error || 
                err.response?.data?.error || 
                err.message || 
                'Failed to verify OTP code'
            );
        }
        
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleNewPasswordVisibility = () => {
        setShowNewPassword(!showNewPassword);
    };


    // if we require new password
    if (newPasswordRequired) {
        return (
            <form className="mb-6" onSubmit={handleNewPasswordSubmit}>
                <div className="mb-6">
                    <h4>Set New Password</h4>
                    <p>Please set a new password for your account.</p>
                </div>
                
                {error && <div className="alert alert-danger mb-4">{error}</div>}
                
                
                
                <div className="mb-6 form-control-validation">
                    <label className="form-label" htmlFor="firstName">First Name</label>
                    <input
                        type="text"
                        id="firstName"
                        className="form-control"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                    />
                </div>
                
                <div className="mb-6 form-control-validation">
                    <label className="form-label" htmlFor="lastName">Last Name</label>
                    <input
                        type="text"
                        id="lastName"
                        className="form-control"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                    />
                </div>

                <div className="mb-6 form-password-toggle form-control-validation">
                    <label className="form-label" htmlFor="newPassword">New Password</label>
                    <div className="input-group input-group-merge">
                        <input
                            type={showNewPassword ? "text" : "password"}
                            id="newPassword"
                            name="newPassword"
                            placeholder="············"
                            aria-describedby="newPassword"
                            className="form-control"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                        />
                        <span className="input-group-text cursor-pointer" onClick={toggleNewPasswordVisibility}>
                            <i className={`icon-base ti ${showNewPassword ? "tabler-eye" : "tabler-eye-off"}`}></i>
                        </span>
                    </div>
                </div>
                
                <button 
                    className="btn btn-primary d-grid w-100" 
                    type="submit"
                    disabled={isLoading}
                >
                    {isLoading ? 'Setting Password...' : 'Set New Password'}
                </button>
            </form>
        );
    }

    // New OTP form to render when MFA is required
    if (requiresMFA) {
        return (
            <form className="mb-6" onSubmit={handleOTPSubmit}>
                <div className="mb-6">
                    <h4>Email Verification Code</h4>
                    <p>Please enter the verification code sent to your email.</p>
                </div>
                
                {error && <div className="alert alert-danger mb-4">{error}</div>}
                
                <div className="mb-6 form-control-validation">
                    <label className="form-label" htmlFor="otpCode">Verification Code</label>
                    <input
                        type="text"
                        id="otpCode"
                        className="form-control"
                        placeholder="Enter verification code"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value)}
                        required
                        autoFocus
                    />
                </div>
                
                <button 
                    className="btn btn-primary d-grid w-100" 
                    type="submit"
                    disabled={isLoading}
                >
                    {isLoading ? 'Verifying...' : 'Verify Code'}
                </button>
            </form>
        );
    }

    // Standard login form
    return (
        <>
            <h4 className="mb-1">Welcome to Parki! 👋</h4>
            <p className="mb-6">Please sign-in to your account</p>
            <form id="formAuthentication" className="mb-6" onSubmit={handleSubmit}>
                {error && <div className="alert alert-danger mb-4">{error}</div>}
                
                <div className="mb-6 form-control-validation">
                    <label htmlFor="email" className="form-label">Email</label>
                    <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="email-username"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoFocus
                        required
                    />
                </div>
                
                <div className="mb-6 form-password-toggle form-control-validation">
                    <label className="form-label" htmlFor="password">Password</label>
                    <div className="input-group input-group-merge">
                        <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            className="form-control"
                            name="password"
                            placeholder="············"
                            aria-describedby="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                        <span 
                            className="input-group-text cursor-pointer"
                            onClick={togglePasswordVisibility}
                        >
                            <i className={`icon-base ti ${showPassword ? "tabler-eye" : "tabler-eye-off"}`}></i>
                        </span>
                    </div>
                </div>
                
                <div className="my-8">
                    <div className="d-flex justify-content-between">
                        <div className="mb-0 ms-2"></div>
                        <Link to="/forgot-password">
                            <p className="mb-0">Forgot Password?</p>
                        </Link>
                    </div>
                </div>
                
                <button 
                    className="btn btn-primary d-grid w-100" 
                    type="submit"
                    disabled={isLoading}
                >
                    {isLoading ? 'Signing in...' : 'Sign in'}
                </button>
            </form>

            {/* Rest of the component remains the same */}
            <p className="text-center">
                <span>New on our platform?</span>
                <a href="https://parki.cloud/?get_started=true" target="_blank" rel="noopener noreferrer">
                    <span> Talk to us</span>
                </a>
            </p>

            {/* <div className="divider my-6">
                <div className="divider-text">or</div>
            </div> */}


        </>
    );
};

export default LoginForm;